import { css, SerializedStyles } from '@emotion/react';
import { Fragment, useContext } from 'react';
import { useSelector } from 'react-redux';
import moment from 'moment';

import t from 'react-translate';
import { AngularContext } from 'react-app';
import Avatar from 'athena/components/mentoring-program/avatar';
import prodPathReplace from 'shared/prod-path-rewrite';
import NvIcon from 'shared/components/nv-icon';
import {
  NvTableCellProps,
  NvTableRowProps,
} from 'shared/components/nv-responsive-table';
import { createGridStyles, standardSpacing } from 'styles/global_defaults/scaffolding';

import { getCurrentUser } from 'redux/selectors/users';
import { MentoringProgramMatchingType } from 'redux/schemas/models/mentoring-programs';
import { coverImgStyles } from './styles';
import { MenteeInfo, OfferingTypes } from './types';


type BasicMenteeCellProps = NvTableCellProps<MenteeInfo, unknown> & {
  course: MenteeInfo;
};

type CellProps = {
  children: React.ReactNode;
  rowKey?: string;
  reactKey: string;
  serializedStyles: SerializedStyles;
  divProps: React.HTMLAttributes<HTMLDivElement>;
  onClick?: () => void;
  rowIndex: number;
};

const Cell = ({
  children,
  rowKey,
  reactKey,
  serializedStyles,
  divProps,
  onClick,
  rowIndex,
}: CellProps) => (
  <div
    className={rowKey}
    css={serializedStyles}
    key={reactKey}
    {...divProps}
    onClick={onClick}
    data-qa={`${rowKey}-${rowIndex}`}
    role='button'
    tabIndex={0}
  >
    {children}
  </div>
);

const CourseNameCell = (props: BasicMenteeCellProps) => {
  const { course } = props;
  const { injectServices } = useContext(AngularContext);
  const [$state] = injectServices(['$state']);
  const { currentInstitutionId } = useSelector(state => state.app);
  const currentUser = useSelector(getCurrentUser);
  const institutionBrandColor = useSelector(
    state => state.models.institutions[currentInstitutionId]?.brandColor,
  );

  let url: string;
  let coverImage: string;

  if (course.type === OfferingTypes.MENTORSHIP_PROGRAM) {
    if (course.mentorshipProgramEnrollment.assignedConnections.length > 1) {
      url = $state.href('mentoring-program-my-mentees', {
        programId: course.id,
      });
    } else if (course.mentorshipProgramEnrollment.assignedConnections.length === 1) {
      url = $state.href('mentoring-program-single-connection', {
        programId: course.id,
        connectionId: course.mentorshipProgramEnrollment.assignedConnections[0].connectionId,
      });
    } else {
      url = $state.href('mentoring-program-root', {
        programId: course.id,
      });
    }
    coverImage = course.headerBackground || prodPathReplace('images/mentoring-program-header-background-base-layer.png');
  } else {
    const goToCourseUrl = $state.href('course', {
      catalogId: course.catalogId!,
    });
    url = `${goToCourseUrl}/mentees/User/${currentUser.id}`;
    coverImage = course.thumbnail && !course.thumbnail.includes('temp.png') ? course.thumbnail : '';
  }

  return (
    <div
      className='course-name-cell'
      css={props.serializedStyles}
      key={props.reactKey}
      {...props.divProps}
    >
      <a
        href={url}
        rel='noopener noreferrer'
        className='cover-image-container'
        {...(course.catalogId && { 'data-offering-name': course.catalogId })}
      >
        <div className='image' css={coverImgStyles(coverImage, institutionBrandColor, course.type === OfferingTypes.MENTORSHIP_PROGRAM)}>
          {course.type === OfferingTypes.MENTORSHIP_PROGRAM && (
            <div className='mentor-plus-diamond'>
              <NvIcon icon='mentor-plus' size='smallest' className='text-white' />
            </div>
          )}
        </div>
        <div>
          <p className='title'>{course.name}</p>
          {course.catalogId && <p className='course-catalog-id'>{course.catalogId}</p>}
        </div>
      </a>
    </div>
  );
};

const MenteesCell = (props: BasicMenteeCellProps) => {
  const { course } = props;

  if (course.type === OfferingTypes.MENTORSHIP_PROGRAM) {
    if (course.mentorshipProgramEnrollment.assignedConnections.length === 0
      || (course.matchingType === MentoringProgramMatchingType.AUTOMATED_MATCHING
        && !(
          course.matchAnnouncementDate
          && moment().isAfter(moment(course.matchAnnouncementDate))
        ))
    ) {
      return (
        <Cell {...props}>
          {t.SUPERVISOR_ADMIN_DASHBOARD.TABLE.PENDING_MENTEES({ MenteeAlias: course.programAlias.mentee.singularizedTitleized })}
        </Cell>
      );
    }

    if (course.mentorshipProgramEnrollment.assignedConnections.length === 1) {
      const { user } = course.mentorshipProgramEnrollment.assignedConnections[0].assignedEnrollment;
      return (
        <Cell {...props}>
          <Avatar user={user} className='mr-1 avatar' />
          {t.USERS.FULL_NAME(user.firstName, user.lastName)}
        </Cell>
      );
    }

    return (
      <Cell {...props}>{course.mentorshipProgramEnrollment.assignedConnections.length} {course.programAlias.mentee.pluralizedTitleized}</Cell>
    );
  }

  const { menteeAlias } = course;
  const suffix = (!course.totalNoOfMentees || course.totalNoOfMentees > 1) ? menteeAlias.pluralizedTitleized : menteeAlias.singularizedTitleized;
  return (
    <Cell {...props}>{course.totalNoOfMentees} {suffix}</Cell>
  );
};

const createCellProps = (
  props: NvTableRowProps<MenteeInfo, {}>,
  key: string,
  i: number,
  rowIndex: number,
): BasicMenteeCellProps => ({
  ...props,
  reactKey: `${rowIndex}-${i + 1}`,
  serializedStyles: css`
    .avatar {
      height: ${standardSpacing}px;
      width: ${standardSpacing}px;
    }
  `,
  rowKey: key,
  divProps: {
    style: createGridStyles(i + 1, rowIndex, i + 2, rowIndex + 1),
  },
  course: props.data,
});

const SupervisorCourseRow = (props: NvTableRowProps<MenteeInfo, {}>) => {
  const cells: [string, (props: BasicMenteeCellProps) => JSX.Element][] = [
    ['course-name-cell', CourseNameCell],
    ['mentees-cell', MenteesCell],
  ];

  return (
    <Fragment>
      {cells.map((Item, i) => {
        const [key, Component] = Item;
        const cellProps = createCellProps(props, key, i, props.rowIndex);
        return <Component key={key} {...cellProps} />;
      })}
    </Fragment>
  );
};

export default SupervisorCourseRow;
